import { NumericInput, Radio, RadioGroup, Tag } from "@blueprintjs/core"
import { DateInput } from "@blueprintjs/datetime"
import dayjs from "dayjs"
import { chunk, pick } from "lodash-es"
import React from "react"
import { RRule, Options } from "rrule"
import classNames from "utils/classNames"
import { RuleStrType } from "."
import CustomFormGroup from "../CustomFormGroup"
import StaticSelector from "../StaticSelector"
import { DAYS, FREQUENCY_OPTIONS, MONTHS, WEEKDAYS } from "./constants"
import RuleWrapper from "./RuleWrapper"

type SingleRuleType = {
  ruleType: {
    rule: RRule
    type: RuleStrType
  }
  onDelete?: (index) => void
  index: number
  isOpen?: boolean
  onOpen?: (index?: number) => void
  onChange?: (rule: RRule) => void
  onTypeChange?: (type: RuleStrType) => void
}
const SingleRule = ({
  ruleType,
  index,
  onDelete,
  isOpen,
  onOpen,
  onChange,
  onTypeChange,
}: SingleRuleType) => {
  const rule = ruleType.rule
  const frequency = FREQUENCY_OPTIONS.find(
    option => option.id === rule.options.freq
  )

  function updateRule(newOptions?: Partial<Options>) {
    onChange(
      new RRule(
        pick({ ...rule.options, ...newOptions }, [
          "freq",
          "interval",
          "byweekday",
          "bymonthday",
          "bymonth",
          "count",
          "until",
        ])
      )
    )
  }

  function renderBody() {
    switch (rule.options.freq) {
      case RRule.WEEKLY:
        return chunk(WEEKDAYS, 7).map((row, rowId) => (
          <tr key={rowId}>
            {row.map((day, colId) => {
              const dayId = day.id.weekday
              const isSelected = rule.options.byweekday.includes(dayId)

              const weekdays = isSelected
                ? rule.options.byweekday.filter(d => d !== dayId)
                : rule.options.byweekday.concat(dayId)

              return (
                <td
                  onClick={() => {
                    if (weekdays.length > 0)
                      updateRule({
                        byweekday: weekdays,
                      })
                  }}
                  className={classNames(
                    "px-3 py-2 border-legacy cursor-pointer",
                    isSelected ? "bg-green-100" : "hover:bg-gray-100"
                  )}
                  key={`${rowId}-${colId}`}
                >
                  {day.name}
                </td>
              )
            })}
          </tr>
        ))
      case RRule.MONTHLY:
        return chunk(DAYS, 7).map((row, rowId) => {
          return (
            <tr key={rowId}>
              {row.map((day, colId) => {
                const monthDays = rule.options.bymonthday
                const dayId = day.id
                const isSelected = monthDays.includes(dayId)

                const newMonthDays = isSelected
                  ? monthDays.filter(d => d !== dayId)
                  : monthDays.concat(dayId)

                return (
                  <td
                    onClick={() => {
                      if (newMonthDays.length > 0)
                        updateRule({
                          bymonthday: newMonthDays,
                        })
                    }}
                    className={classNames(
                      "px-3 py-2 border-legacy cursor-pointer",
                      isSelected ? "bg-green-100" : "hover:bg-gray-100"
                    )}
                    key={`${rowId}-${colId}`}
                  >
                    {day.name}
                  </td>
                )
              })}
            </tr>
          )
        })

      case RRule.YEARLY:
        return chunk(MONTHS, 4).map((row, rowId) => {
          return (
            <tr key={rowId}>
              {row.map((month, colId) => {
                const allMonths = rule.options.bymonth || []
                const monthId = month.id
                const isSelected = allMonths.includes(monthId)

                const newMonthDays = isSelected
                  ? allMonths.filter(d => d !== monthId)
                  : allMonths.concat(monthId)

                return (
                  <td
                    onClick={() => {
                      if (newMonthDays.length > 0)
                        updateRule({
                          bymonth: newMonthDays,
                        })
                    }}
                    className={classNames(
                      "px-3 py-2 border-legacy cursor-pointer",
                      isSelected ? "bg-green-100" : "hover:bg-gray-100"
                    )}
                    key={`${rowId}-${colId}`}
                  >
                    {month.name}
                  </td>
                )
              })}
            </tr>
          )
        })
      default:
        break
    }
  }

  return (
    <RuleWrapper
      ruleType={ruleType.type}
      isOpen={isOpen}
      onOpen={onOpen}
      index={index}
      onDelete={onDelete}
      onTypeChange={onTypeChange}
      title={rule.toText()}
    >
      <div>
        <div id="rule-frequency" className="mb-3 flex">
          <StaticSelector
            className="mr-4"
            value={frequency}
            onChange={(_, value) => {
              switch (value.id) {
                case RRule.YEARLY:
                  return updateRule({
                    freq: value.id,
                    byweekday: null,
                    bymonthday: null,
                  })
                case RRule.MONTHLY:
                  return updateRule({
                    freq: value.id,
                    bymonth: null,
                    byweekday: null,
                  })

                case RRule.WEEKLY:
                  return updateRule({
                    freq: value.id,
                    bymonthday: null,
                    bymonth: null,
                  })
                case RRule.DAILY:
                  return updateRule({
                    freq: value.id,
                    bymonthday: null,
                    bymonth: null,
                    byweekday: null,
                  })
                default:
                  break
              }
            }}
            items={FREQUENCY_OPTIONS}
            label="Frequency"
            inline
          />
          <CustomFormGroup label="Every" inline big={false}>
            <NumericInput
              min={1}
              type="number"
              value={rule.options.interval || 1}
              rightElement={<Tag>{frequency.unit}</Tag>}
              onValueChange={value => {
                if (value >= 1) {
                  updateRule({ interval: value })
                }
              }}
            />
          </CustomFormGroup>
        </div>
        <div id="rule-selection" className="mb-3">
          <table className="border-legacy border-collapse text-sm">
            {renderBody()}
          </table>
        </div>
        <RadioGroup
          data-testid="rule-ending"
          className="label-mb-1 rule-ending"
          inline
          selectedValue={
            rule.options.count ? "count" : rule.options.until ? "date" : "never"
          }
          onChange={e => {
            const value = e.currentTarget.value
            switch (value) {
              case "never":
                updateRule({ until: null, count: null })
                return

              case "count":
                updateRule({ until: null, count: 1 })

                return
              case "date":
                updateRule({ until: dayjs().toDate(), count: null })
                return
            }
          }}
          label="Ends On"
        >
          <Radio label="never" value="never" />
          <Radio
            labelElement={
              <div className="radio-input">
                <span className="mr-1">on date</span>{" "}
                <DateInput
                  maxDate={dayjs().add(5, "year").toDate()}
                  formatDate={date => dayjs(date).format("DD/DD/YYYY")}
                  inputProps={{ leftIcon: "calendar" }}
                  value={rule.options.until}
                  onChange={date => {
                    updateRule({
                      until: date,
                      count: null,
                    })
                  }}
                />
              </div>
            }
            value="date"
          />
          <Radio
            labelElement={
              <div className="radio-input">
                <span>after</span>{" "}
                <NumericInput
                  min={1}
                  className="mx-1"
                  type="text"
                  value={rule.options.count || 1}
                  onValueChange={num => {
                    if (num >= 1) {
                      updateRule({ count: num, until: null })
                    }
                  }}
                  rightElement={<Tag>Occurences</Tag>}
                />
              </div>
            }
            value="count"
          />
        </RadioGroup>
      </div>
    </RuleWrapper>
  )
}

export default SingleRule
